import { useState, useEffect } from 'react';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import {
  User,
  Mail,
  Lock,
  Shield,
  Save,
  Loader2,
  Camera,
  Eye,
  EyeOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminProfile = () => {
  const { isDarkMode } = useAdminTheme();
  const { user, admin, refreshUser } = useAdminAuth();
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user || admin) {
      setFormData(prev => ({
        ...prev,
        name: user?.name || admin?.name || '',
        email: user?.email || admin?.email || ''
      }));
    }
  }, [user, admin]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API}/admin/profile`, {
        name: formData.name,
        email: formData.email
      }, { withCredentials: true });
      
      toast.success('Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API}/admin/change-password`, {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      }, { withCredentials: true });
      
      toast.success('Password changed successfully');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const userRole = admin?.role || 'super_admin';

  return (
    <div className="max-w-2xl mx-auto" data-testid="admin-profile-page">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Profile Settings
        </h1>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
          Manage your account information and security settings
        </p>
      </div>

      {/* Profile Card */}
      <div className={`rounded-xl border p-6 mb-6 ${
        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
      }`}>
        {/* Avatar Section */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-teal-500 flex items-center justify-center">
              <User className="w-10 h-10 text-white" />
            </div>
            <button className={`absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
              isDarkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'
            }`}>
              <Camera className={`w-4 h-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`} />
            </button>
          </div>
          <div>
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {formData.name || 'Admin User'}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <Shield className="w-4 h-4 text-blue-500" />
              <span className="text-blue-500 text-sm font-medium">
                {userRole.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleUpdateProfile}>
          <div className="space-y-4">
            <div>
              <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Full Name</Label>
              <div className="relative mt-1">
                <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`pl-10 ${
                    isDarkMode 
                      ? 'bg-slate-700 border-slate-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Enter your name"
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            <div>
              <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Email Address</Label>
              <div className="relative mt-1">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 ${
                    isDarkMode 
                      ? 'bg-slate-700 border-slate-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Enter your email"
                  data-testid="profile-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </div>

      {/* Change Password Card */}
      <div className={`rounded-xl border p-6 ${
        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
      }`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Change Password
        </h3>
        
        <form onSubmit={handleChangePassword}>
          <div className="space-y-4">
            <div>
              <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Current Password</Label>
              <div className="relative mt-1">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <Input
                  name="currentPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={handleChange}
                  className={`pl-10 pr-10 ${
                    isDarkMode 
                      ? 'bg-slate-700 border-slate-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                    isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>New Password</Label>
              <div className="relative mt-1">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <Input
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={handleChange}
                  className={`pl-10 pr-10 ${
                    isDarkMode 
                      ? 'bg-slate-700 border-slate-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Enter new password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                    isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Confirm New Password</Label>
              <div className="relative mt-1">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
                <Input
                  name="confirmPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`pl-10 ${
                    isDarkMode 
                      ? 'bg-slate-700 border-slate-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.currentPassword || !formData.newPassword}
              variant="outline"
              className={`w-full ${
                isDarkMode 
                  ? 'border-slate-600 text-slate-300 hover:bg-slate-700' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="change-password-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Change Password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;
